import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { getSessionStorageItem, setSessionStorageItem } from "@tm/utility";
import { useEffect, useRef, useState } from "react";
import { ChatbotIcon, ChatbotIframe } from "./Chatbot.style";
import ChatbotPersuasionText from "./ChatbotPersuasionText";

interface ChatBotProp {
  myraUrl: string;
  pageName?: string;
  chatbotBottomMargin?: number;
}

const Chatbot = ({
  myraUrl,
  pageName,
  chatbotBottomMargin = 40,
}: ChatBotProp) => {
  const [showChatBot, setShowChatBot] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const device = useBreakpointV2();
  const analytics = getAnalytics();
  const ref = useRef<HTMLDivElement | null>(null);
  const imageUrl =
    "https://imgak.mmtcdn.com/flights/assets/media/mobile/listing/3X/myra_new_gif.webp?v=2";
  const { type } = usePlanType();

  const handleShowAnimation = () => {
    let showAnimationTime = getSessionStorageItem("showAnimationTime") || "";
    const currentTime = new Date().getTime();
    if (
      !showAnimationTime ||
      currentTime - parseInt(showAnimationTime) >= 3600000
    ) {
      showAnimationTime = currentTime.toString();
      setSessionStorageItem("showAnimationTime", showAnimationTime);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (pageName && pageName === "LANDING" && device === "lg") {
      const sessionAnimation = handleShowAnimation();
      setShowAnimation(sessionAnimation);

      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [pageName, device]);

  const getMyraAppLinkOrigin = () => {
    try {
      return new URL(myraUrl).origin;
    } catch (e) {
      return "Error getting myra url origin";
    }
  };

  const handleMessage = (event: MessageEvent) => {
    // Ensure the message comes from the correct origin
    if (event.origin !== getMyraAppLinkOrigin()) {
      return;
    }

    const { type } = event.data || {};
    if (
      type === "CHAT_BOT__BRIDGE__MINIMIZE_VIEW" ||
      type === "CHAT_BOT__BRIDGE__CLOSE_VIEW"
    ) {
      setShowChatBot(false);
    }
  };

  const handleClickOutside = (event: Event) => {
    if (ref?.current && !ref?.current?.contains(event.target as Node)) {
      setShowChatBot(false);
    }
  };

  const addChatbotAnalytics = (
    eventName: "chatbot_shown" | "chatbot_clicked"
  ) => {
    if (pageName) {
      trackEvent({
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: eventName,
        },
      });
      switch (pageName) {
        case "LANDING":
          analytics?.trackLandingEvents(
            { actionType: eventName },
            "pageView",
            type
          );
          break;
        case "LISTING":
          analytics?.trackListingEvents(
            { actionType: eventName },
            "pageView",
            type
          );
          break;
        case "ADD_TRAVELLERS":
          analytics?.trackTravDetailsEvents(
            { actionType: eventName },
            "pageView",
            type
          );
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    // Attach event listener to listen for messages from the iframe
    window.addEventListener("message", handleMessage);
    addChatbotAnalytics("chatbot_shown");
    // Cleanup on component unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (device === "sm") {
      if (showChatBot) {
        document.body.style.overflow = "hidden";
      }
      if (!showChatBot) {
        document.body.style.overflow = "auto";
      }
    }

    if (showChatBot) {
      document.addEventListener("click", handleClickOutside, false);
    } else {
      document.removeEventListener("click", handleClickOutside, false);
    }
  }, [showChatBot]);

  const chatbotClickHandler = () => {
    setShowChatBot(true);
    addChatbotAnalytics("chatbot_clicked");
  };

  return (
    <div ref={ref}>
      {!showAnimation && pageName === "LANDING" && device === "lg" ? (
        <ChatbotPersuasionText src={imageUrl} onClick={chatbotClickHandler} />
      ) : (
        <ChatbotIcon
          src={imageUrl}
          alt="Myra Animation"
          pageName={pageName}
          onClick={chatbotClickHandler}
          showAnimation={showAnimation}
          chatbotBottomMargin={chatbotBottomMargin}
        />
      )}

      {showChatBot && (
        <ChatbotIframe
          device={device}
          src={myraUrl}
          title="myraChatBot"
          pageName={pageName}
        />
      )}
    </div>
  );
};

export default Chatbot;
